import { Link } from 'react-router-dom';
import { agents } from '../../../mocks/agents';
import { useEffect, useState, memo, useMemo } from 'react';
import { imageOptimizer } from '../../../utils/imageOptimization';

interface AgentsGridProps {
  agents?: typeof agents;
}

function AgentsGrid({ agents: agentsProp }: AgentsGridProps) {
  const displayAgents = useMemo(() => agentsProp || agents, [agentsProp]);
  const [cachedImages, setCachedImages] = useState<Record<string, string>>({});

  useEffect(() => {
    // Précharger toutes les images des agents
    const imageUrls = displayAgents.map(agent => agent.image);
    imageOptimizer.preloadImages(imageUrls);

    // Charger les images optimisées
    displayAgents.forEach(agent => {
      imageOptimizer.getOptimizedImage(agent.image).then(optimizedUrl => {
        setCachedImages(prev => ({
          ...prev,
          [agent.id]: optimizedUrl
        }));
      });
    });
  }, [displayAgents]);

  return (
    <section id="agents-grid" className="py-32 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-cyber-purple/5 to-transparent"></div>
      
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center mb-20 animate-fade-in-up">
          <h2 className="text-5xl lg:text-6xl font-display font-bold text-white mb-4">
            10 Agents IA
          </h2>
          <p className="text-5xl lg:text-6xl font-display font-bold bg-gradient-to-r from-cyber-blue to-[#39FF14] bg-clip-text text-transparent">
            Pour Chaque Fonction
          </p>
        </div>

        {/* Agents Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayAgents.map((agent, index) => (
            <div
              key={agent.id}
              className="animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <Link
                to={`/agents/${agent.slug}`}
                className="group block p-8 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl border border-white/10 hover:border-white/20 transition-all cursor-pointer h-full"
              >
                <div className="relative h-64 overflow-hidden rounded-t-2xl">
                  {cachedImages[agent.id] ? (
                    <img
                      src={cachedImages[agent.id]}
                      alt={agent.name}
                      width="400"
                      height="256"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110 will-change-transform"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-cyber-blue/20 to-cyber-purple/20 animate-pulse"></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 left-4 w-14 h-14 flex items-center justify-center bg-white/95 backdrop-blur-sm rounded-xl shadow-lg">
                    <i className={`${agent.icon} text-2xl text-gray-900`}></i>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-4 right-4 px-3 py-1 bg-black/80 backdrop-blur-sm rounded-full text-xs font-semibold text-white">
                    {agent.category}
                  </div>
                </div>

                <h3 className="text-2xl font-display font-bold text-white mb-3 group-hover:text-cyber-blue transition-colors">
                  {agent.name}
                </h3>

                <p className="text-gray-400 font-body text-sm mb-6 leading-relaxed">
                  {agent.description}
                </p>

                <div className="space-y-2 mb-6">
                  {agent.features.slice(0, 3).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <i className="ri-check-line text-sm mt-0.5" style={{ color: agent.color }}></i>
                      <span className="text-xs text-gray-500 font-body">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-white/10">
                  <div>
                    <span className="text-xs text-gray-500 font-body">À partir de</span>
                    <div className="flex items-end gap-1">
                      <span className="text-2xl font-display font-bold text-white">{agent.price.basic}€</span>
                      <span className="text-xs text-gray-500 font-body mb-1">/mois</span>
                    </div>
                  </div>
                  <div 
                    className="w-10 h-10 flex items-center justify-center rounded-full transition-all group-hover:translate-x-1"
                    style={{ backgroundColor: `${agent.color}20` }}
                  >
                    <i className="ri-arrow-right-line text-lg" style={{ color: agent.color }}></i>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default memo(AgentsGrid);